/**
 * The form cards both schedule drawers render (docs/DUPLICATION_AUDIT.md, B5), driven by
 * useScheduleForm. ScheduleReportDrawer (create) and ScheduleDrawer (edit) keep their own
 * shells, read-only rows, Reported Period card and payloads.
 *
 * `readOnly` is the edit drawer's view for users who may not manage the report: they can
 * still change User Recipients (within the role gate), and nothing else.
 */

import { useMemo, useCallback } from 'react';
import '../scheduled-reports-page.css';
import '../../alert-details/alert-details-page.css';

const WEEKLY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHLY_DAYS = [...Array.from({ length: 31 }, (_, i) => i + 1), 0]; // 1-31 + 0 = last day

/**
 * Description, delivery rate, days and send time. `before` renders above the description
 * and `after` below it, for each drawer's read-only rows.
 */
export function ScheduleDetailsCard({ form, frequencies, idPrefix, readOnly = false, before = null, after = null }) {
    return (
        <div className="sr-form-card">
            <h3 className="sr-form-card__title">Schedule Details</h3>
            {before}

            <div className="sr-form-section">
                <label htmlFor={`${idPrefix}-description`} className="sr-form-section__label">Description</label>
                <input
                    id={`${idPrefix}-description`}
                    type="text"
                    className="sr-email-input"
                    value={form.description}
                    onChange={(e) => form.setDescription(e.target.value)}
                    placeholder="e.g. Daily Alert Summary"
                    required
                    disabled={readOnly}
                    readOnly={readOnly}
                />
            </div>
            {after}

            <div className="sr-form-section">
                <label htmlFor={`${idPrefix}-frequency`} className="sr-form-section__label">Delivery Rate</label>
                <select
                    id={`${idPrefix}-frequency`}
                    className="sr-time-input"
                    value={form.frequencyId}
                    onChange={(e) => form.setFrequencyId(parseInt(e.target.value, 10))}
                    style={{ width: '100%' }}
                    disabled={readOnly}
                >
                    {(frequencies || []).map((f) => (
                        <option key={f.window_frequency_id} value={f.window_frequency_id}>{f.frequency_name}</option>
                    ))}
                </select>
            </div>

            {form.frequencyId === 2 ? (
                <div className="sr-form-section">
                    <span className="sr-form-section__label">Days of Week</span>
                    <div className="sr-day-picker">
                        {WEEKLY_DAYS.map((day, idx) => (
                            <button key={idx} type="button" className={`sr-day-chip${form.selectedDays.has(String(idx)) ? ' is-selected' : ''}`} onClick={() => form.toggleDay(idx)} disabled={readOnly}>{day}</button>
                        ))}
                    </div>
                </div>
            ) : null}

            {form.frequencyId === 3 ? (
                <div className="sr-form-section">
                    <span className="sr-form-section__label">Days of Month</span>
                    <div className="sr-day-picker">
                        {MONTHLY_DAYS.map((day) => (
                            <button key={day} type="button" className={`sr-day-chip${form.selectedDays.has(String(day)) ? ' is-selected' : ''}`} onClick={() => form.toggleDay(day)} disabled={readOnly}>{day === 0 ? 'Last' : day}</button>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* "Delivered", so it cannot be read as the reporting window, which is also a
                time of day. The create drawer said just "Time of Day". */}
            <div className="sr-form-section">
                <label htmlFor={`${idPrefix}-sendtime`} className="sr-form-section__label">Delivered Time of Day</label>
                <input
                    id={`${idPrefix}-sendtime`}
                    type="time"
                    className="sr-time-input"
                    value={form.sendTime}
                    onChange={(e) => form.setSendTime(e.target.value)}
                    step={300}
                    disabled={readOnly}
                    readOnly={readOnly}
                />
            </div>
        </div>
    );
}

/** Attachments, then the drawer's Applied Filters section as children. */
export function ScheduleAttachmentsCard({ form, showCsv, readOnly = false, children = null }) {
    return (
        <div className="sr-form-card">
            <h3 className="sr-form-card__title">Attachments &amp; Filters</h3>
            <div className="sr-form-section">
                <span className="sr-form-section__label">Attachments</span>
                <div className="sr-attachments">
                    <label className="sr-attachment-item">
                        <input type="checkbox" checked={form.pdfSummary} onChange={(e) => form.setPdfSummary(e.target.checked)} disabled={readOnly} />
                        PDF Attachment (Summary)
                    </label>
                    <label className="sr-attachment-item">
                        <input type="checkbox" checked={form.pdfDetails} onChange={(e) => form.setPdfDetails(e.target.checked)} disabled={readOnly} />
                        PDF Attachment (Details)
                    </label>
                    {showCsv ? (
                        <label className="sr-attachment-item">
                            <input type="checkbox" checked={form.csvDetails} onChange={(e) => form.setCsvDetails(e.target.checked)} disabled={readOnly} />
                            CSV Attachment (Details)
                        </label>
                    ) : null}
                </div>
            </div>
            {children}
        </div>
    );
}

/** The chip list under "Applied Filters"; `action` sits beside the label, `sublabel` under it. */
export function AppliedFilters({ chips, action = null, sublabel = null }) {
    return (
        <div className="sr-form-section">
            {action ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                    <span className="sr-form-section__label" style={{ marginBottom: 0 }}>Applied Filters</span>
                    {action}
                </div>
            ) : (
                <span className="sr-form-section__label">Applied Filters</span>
            )}
            {sublabel ? <span className="sr-form-section__sublabel">{sublabel}</span> : null}
            <div className="sr-filters-summary">
                {chips.length === 0 ? <span className="sr-no-filters">No filters applied</span> : null}
                {chips.map((chip) => (
                    <span key={chip.key} className="sr-filter-tag">
                        <span className="sr-filter-tag__label">{chip.label}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

/**
 * User Recipients (role-gated by useRecipientGate) and Email Recipients.
 *
 * Clear removes only the users you outrank: a higher-ranked recipient already on an
 * edited schedule is not yours to remove. Select All adds the users you outrank who
 * have an email on file, to the current selection.
 */
export function ScheduleRecipientsCard({ form, users, gate, readOnly = false }) {
    const { canManageUser, weightsLoaded } = gate;
    const { selectedUserIds, setSelectedUserIds, userSearch } = form;

    const filteredUsers = useMemo(() => {
        if (!userSearch) return users;
        const term = userSearch.toLowerCase();
        return users.filter((u) =>
            (u.display_name || '').toLowerCase().includes(term) ||
            (u.email || '').toLowerCase().includes(term));
    }, [users, userSearch]);

    const toggleUser = useCallback((id) => {
        const user = users.find((u) => String(u.user_id) === id);
        if (!user || !canManageUser(user)) return;
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, [users, canManageUser, setSelectedUserIds]);

    const selectAll = useCallback(() => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            filteredUsers.forEach((u) => { if (u.email && canManageUser(u)) next.add(String(u.user_id)); });
            return next;
        });
    }, [filteredUsers, canManageUser, setSelectedUserIds]);

    const clearUsers = useCallback(() => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            users.forEach((u) => { if (canManageUser(u)) next.delete(String(u.user_id)); });
            return next;
        });
    }, [users, canManageUser, setSelectedUserIds]);

    return (
        <div className="sr-form-card">
            <h3 className="sr-form-card__title">Recipients</h3>

            <div className="sr-form-section">
                <span className="sr-form-section__label">User Recipients</span>
                <span className="sr-form-section__sublabel">
                    Select users whose email addresses will receive this report
                    {selectedUserIds.size > 0 ? ` (${selectedUserIds.size} selected)` : ''}
                </span>
                <div className="ar-filter-section" style={{ marginTop: '8px' }}>
                    <div className="ar-filter-section__header">
                        <span className="ar-filter-section__label">Users</span>
                        <div className="ar-filter-section__actions">
                            <button className="ar-filter-section__btn" onClick={selectAll} type="button">Select All</button>
                            <button className="ar-filter-section__btn" onClick={clearUsers} type="button">Clear</button>
                        </div>
                    </div>
                    <input
                        className="ar-filter-section__search"
                        type="text"
                        placeholder="Search users..."
                        value={userSearch}
                        onChange={(e) => form.setUserSearch(e.target.value)}
                    />
                    <div className="ar-filter-list">
                        {filteredUsers.length === 0 ? <p className="ar-filter-list__empty">No users found.</p> : null}
                        {filteredUsers.map((user) => {
                            const id = String(user.user_id);
                            const hasEmail = Boolean(user.email);
                            const outranks = canManageUser(user);
                            const disabled = !hasEmail || !outranks;
                            let subtext;
                            if (!hasEmail) subtext = 'No email on file';
                            else if (!weightsLoaded) subtext = `${user.email} (checking permissions…)`;
                            else if (!outranks) subtext = `${user.email} (higher role — cannot modify)`;
                            else subtext = user.email;
                            return (
                                <label
                                    key={id}
                                    className={`ar-filter-list__item ar-filter-list__item--has-subtext${disabled ? ' ar-filter-list__item--disabled' : ''}`}
                                    title={weightsLoaded && !outranks && hasEmail ? 'You cannot add or remove users with a higher role weight.' : undefined}
                                >
                                    <input type="checkbox" checked={selectedUserIds.has(id)} onChange={() => toggleUser(id)} disabled={disabled} />
                                    <span className="ar-filter-list__item-content">
                                        <span className="ar-filter-list__item-name">{user.display_name || '(unknown)'}</span>
                                        <span className="ar-filter-list__item-subtext">{subtext}</span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="sr-form-section">
                <span className="sr-form-section__label">Email Recipients</span>
                <div className="sr-email-list">
                    {form.emails.map((email) => (
                        <span key={email} className="sr-email-chip">
                            {email}
                            {!readOnly ? (
                                <button type="button" className="sr-email-chip__remove" onClick={() => form.removeEmail(email)} aria-label={`Remove ${email}`}>{'✕'}</button>
                            ) : null}
                        </span>
                    ))}
                </div>
                {!readOnly ? (
                    <div className="sr-email-add-row">
                        <input
                            type="email"
                            className="sr-email-input"
                            placeholder="name@example.com"
                            value={form.newEmail}
                            onChange={(e) => form.setNewEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); form.addEmail(); } }}
                        />
                        <button type="button" className="rp-btn rp-btn--secondary" onClick={form.addEmail}>Add</button>
                    </div>
                ) : null}
                {form.emailError ? (
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: '4px', display: 'block' }}>{form.emailError}</span>
                ) : null}
            </div>
        </div>
    );
}
